import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);

export function schoolYearRange(offset = 0) {
  const now = new Date();
  const startYear = (now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1) - offset;
  return { from: `${startYear}-09-01`, to: `${startYear + 1}-08-31`, label: `${startYear}/${String(startYear + 1).slice(2)}` };
}

const PRESETS = [
  { key: "all", label: "All time", range: () => ({ from: "", to: "" }) },
  { key: "this", label: `This school year (${schoolYearRange(0).label})`, range: () => schoolYearRange(0) },
  { key: "last", label: `Last school year (${schoolYearRange(1).label})`, range: () => schoolYearRange(1) },
  { key: "ytd", label: "This calendar year", range: () => ({ from: `${new Date().getFullYear()}-01-01`, to: iso(new Date()) }) },
  { key: "custom", label: "Custom range", range: null },
];

export function buildExportUrl(path, from, to) {
  const params = new URLSearchParams();
  if (from) params.set("date_from", from);
  if (to) params.set("date_to", to);
  const qs = params.toString();
  if (!qs) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${qs}`;
}

export function rangeSuffix(from, to) {
  return from || to ? `_${from || "start"}_to_${to || "today"}` : "";
}

function PresetButtons({ preset, onChoose }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PRESETS.map((p) => (
        <Button key={p.key} size="sm" variant={preset === p.key ? "default" : "outline"} data-testid={`export-preset-${p.key}`} onClick={() => onChoose(p)}>
          {p.label}
        </Button>
      ))}
    </div>
  );
}

function DateField({ label, testid, value, disabled, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type="date" data-testid={testid} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="min-h-[44px]" />
    </div>
  );
}

export default function ExportRangeDialog({ path, filenameBase, testid, label = "Export CSV" }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("this");
  const [from, setFrom] = useState(schoolYearRange(0).from);
  const [to, setTo] = useState(schoolYearRange(0).to);
  const custom = preset === "custom";

  const choose = (p) => {
    setPreset(p.key);
    if (p.range) { const r = p.range(); setFrom(r.from); setTo(r.to); }
  };

  const go = async () => {
    try {
      await downloadCsv(buildExportUrl(path, from, to), `${filenameBase}${rangeSuffix(from, to)}.csv`);
      toast.success("CSV downloaded");
      setOpen(false);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <>
      <Button variant="outline" data-testid={testid} onClick={() => setOpen(true)} className="min-h-[44px]">
        <Download className="w-4 h-4 mr-1.5" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="export-range-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Export {filenameBase}</DialogTitle></DialogHeader>
          <PresetButtons preset={preset} onChoose={choose} />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <DateField label="From" testid="export-from" value={from} disabled={!custom} onChange={setFrom} />
            <DateField label="To" testid="export-to" value={to} disabled={!custom} onChange={setTo} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="export-cancel">Cancel</Button>
            <Button onClick={go} data-testid="export-confirm"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
